<template lang="pug">
#receive-message-setting(v-cloak)
  .box
    .box-header.with-border.register-header
      .title 受信メッセージの設定

    .box-body.register-body
      FormTextarea(key-name="家族ユーザー向け案内文" name="receive-message-guidance-to-families" v-model="receiveMessageGuidanceToFamilies"
        placeholder="例：施設への連絡時にお使いください。ただし返事に時間がかかることもありますので、お急ぎの場合はお電話でご連絡ください。"
        :validation-rules="{ max: 1000 }" :maxlength="1000" :styles="{ maxWidth: '640px', width: '640px', minHeight: '160px' }")
        template(#description)
          .mb-2 家族ユーザーが施設へのメッセージを入力するページに掲載する案内文を編集できます。

      FormTextarea(v-if="_isOverBasicPlan"
        key-name="施設関係者ユーザー向け案内文" name="receive-message-guidance-to-other-user-groups" v-model="receiveMessageGuidanceToOtherUserGroups"
        placeholder="例：施設への連絡時にお使いください。ただし返事に時間がかかることもありますので、お急ぎの場合はお電話でご連絡ください。"
        :validation-rules="{ max: 1000 }" :maxlength="1000" :styles="{ maxWidth: '640px', width: '640px', minHeight: '160px' }")
        template(#description)
          .mb-2 施設関係者ユーザーが施設へのメッセージを入力するページに掲載する案内文を編集できます。

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoReceiveMessageList" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="受信メッセージの設定を更新しますか？" @ok="updateSetting")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoReceiveMessageList")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import FacilitiesService, { FacilityGetModel, FacilityPatchModel } from "@/commons/services/Facilities";
import { PlanType } from "@/commons/services/Plans";

import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";

/**
 * ### 受信メッセージの設定コンポーネント
 */
@Component({
  components: {
    PageLoading,
    ValidationAlert,
    OkModal,
    OkCancelModal,
    FormTextarea
  },
  $_veeValidate: { validator: "new" }
})
export default class ReceiveMessageSetting extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;

  /** エラーメッセージ */
  errorMessage: string = "";

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 受信メッセージ案内文(家族) */
  receiveMessageGuidanceToFamilies: string = "";
  /** 受信メッセージ案内文(施設関係者) */
  receiveMessageGuidanceToOtherUserGroups: string = "";
  /** 更新日時 */
  updated: number = -1;

  /** ### プランがベーシック以上かを返す */
  get _isOverBasicPlan(): boolean {
    const planType: PlanType = userStore.facility.planType;

    if (PlanType.BASIC <= planType) {
      return true;
    } else {
      return false;
    }
  }

  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const facility: FacilityGetModel = userStore.getFacility;
    this.receiveMessageGuidanceToFamilies = facility.receiveMessageGuidanceToFamilies;
    this.receiveMessageGuidanceToOtherUserGroups = facility.receiveMessageGuidanceToOtherUserGroups;
    this.updated = facility.updated;

    this.isBusy = false;

    return;
  }

  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  public async updateSetting(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const param: FacilityPatchModel = await this.createRequestBody();
      await this.facilitiesService.patch(userStore, param);

      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  public createRequestBody(): FacilityPatchModel {
    return {
      name: userStore.getFacility.name,
      tel: userStore.getFacility.tel,
      url: userStore.getFacility.url,
      updated: this.updated,
      receiveMessageGuidanceToFamilies: this.receiveMessageGuidanceToFamilies,
      receiveMessageGuidanceToOtherUserGroups: this.receiveMessageGuidanceToOtherUserGroups
    };
  }

  public gotoReceiveMessageList(): void {
    this.$router.push({ name: PcUrl.ReceiveMessageList });
    return;
  }
}
</script>

<style lang="scss" scoped>
#receive-message-setting {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
